import {
  IDocument,
  IDocumentData,
  IObjectiveArea,
  IProjectDocument,
} from "../../../../core/_models";
import type { ProblemTreeItemLevel } from '../../common/ProblemTree/types';

export interface IProjectOverview {
  project_id: number;
  project_ref_no: string;
  name: string;
  short_name: string;
  department_id: number;
  dept_programme_id: number | null;
  priority_id: string;
  desc: string;
  sector_id: number;
  sub_sector_id: number | null;
  // location_id: number | null;
  district_id: number | null;
  municipality_id: number | null;
  jurisdiction_id: number | null;
  sector_action_id: number | null;
  project_lead: number | null;
  action_project_type_id: number | null;
  project_owner: number | null;
  project_manager: number | null;
  sector_name: string;
  department_name: string;
  project_type_data_key: string[];
  sector_asset_type_id: number[];
  idms_project_stage: string;
  implementation_timeframe: string;
  // Amounts are kept as strings so an empty input stays empty.
  capax: string;
  opax: string;
  ballpark_project_cost: string;
}

export interface IProjectOverviewAction extends IProjectOverview {
  formAction?: string;
  iscompleted?: boolean;
}

// Project Overview tabs, keyed by the API `section`, with the fields each one saves.
export const PROJECT_OVERVIEW_SECTION_FIELDS = {
  basic_information: ['name', 'short_name', 'department_id', 'dept_programme_id', 'project_type_data_key', 'priority_id', 'implementation_timeframe', 'desc', 'capax', 'opax', 'ballpark_project_cost'],
  sector_classification: ['sector_id', 'sub_sector_id', 'sector_action_id', 'action_project_type_id', 'sector_asset_type_id'],
  idms_stage: ['idms_project_stage'],
  accountable_ownership: ['jurisdiction_id', 'district_id', 'municipality_id', 'project_owner', 'project_manager', 'project_lead'],
} as const satisfies Record<string, ReadonlyArray<keyof IProjectOverview>>;

export type ProjectOverviewSection = keyof typeof PROJECT_OVERVIEW_SECTION_FIELDS;

// The tabs in display order.
export const PROJECT_OVERVIEW_SECTIONS = Object.keys(PROJECT_OVERVIEW_SECTION_FIELDS) as ProjectOverviewSection[];

export const PROJECT_OVERVIEW_SECTION_TITLES: Record<ProjectOverviewSection, string> = {
  basic_information: 'Basic Project Information',
  sector_classification: 'Sector Classification',
  idms_stage: 'IDMS Stage (Current Phase)',
  accountable_ownership: 'Accountable & Ownership',
};

export interface IExecutiveSummary {
  project_overview: string;
  vision: string;
  opportunity_definition: string;

  formAction?: string;
  iscompleted?: boolean;
}

export interface IFiveWOneH {
  ips_what: string;
  ips_why: string;
  ips_who: string;
  ips_where: string;
  ips_when: string;
  ips_how: string;

  formAction?: string;
  iscompleted?: boolean;
}

export interface IOrganisationalObjectiveAlignment {
  organisational_goals: string[];
  policy_or_benchmark_alignment: string[];
  implementation_link: string;
  strategic_alignment: string;
  simple_contribution_statement: string;
  alignment_strength: number;

  formAction?: string;
  iscompleted?: boolean;
}

export interface IStep3 {
  project_objectives_areas: IObjectiveArea[];
  formAction?: string;
  iscompleted?: boolean;
}

export interface IProblemIdentification {
  current_state_description: string;
  cost_of_maintaining_status_quo: string;
  quantified_status_quo: string;
  with_project_scenario: string;
  desired_state_quantified: string;
  wcis_wciip_infrastructure_categories: string[];
  wcis_wciip_infrastructure_categories_remarks: Record<string, string>;
  formAction?: string;
  iscompleted?: boolean;
}

// Problem tree items are shared with Stage 1 (common/ProblemTree).
export type { IProblemTreeItem, IProblemTreeResponse, ProblemTreeItemLevel, ProblemTreeItemType } from '../../common/ProblemTree/types';

// Field names match the problem_tree_analysis project-fields section.
export interface IProblemTreeAnalysis {
  focal_problem: string;
  consequences_if_problem_remains_unaddressed: string;
  comprehensive_problem_tree_narrative: string;
  solution_directions_high_level: string[];
  solution_direction_narrative: string;
  formAction?: string;
  iscompleted?: boolean;
}

// Problem Tree tab ids, in display order.
export const PROBLEM_TREE_TABS = {
  focal_problem: 1,
  cause_tree: 2,
  effect_tree: 3,
  summary: 4,
  solution_direction: 5,
} as const;

// Project Objectives and Strategic Alignment tab ids, in display order.
export const OBJECTIVES_TABS = {
  project_objectives: 1,
  means_to_end: 2,
  project_outputs: 3,
  project_outcomes: 4,
  employment_quantification: 5,
  smart_indicators: 6,
  organisational_alignment: 7,
} as const;

// Analysis of Project Resource Requirements tab ids, in display order.
export const RESOURCE_TABS = {
  project_alternatives: 1,
  cost_estimates: 2,
  sources_of_information: 3,
  concept_report_plan: 4,
} as const;

export const RESOURCE_REQUIREMENTS_SECTION = 'project_resource_requirements';

// Preliminary Procurement Strategy tab ids, in display order.
export const PROCUREMENT_TABS = {
  major_inputs: 1,
  procurement_method: 2,
  legislative_alignment: 3,
  procurement_risks: 4,
} as const;

export const PROCUREMENT_STRATEGY_SECTION = 'preliminary_procurement_strategy';

// Stakeholder Analysis tab ids, in display order.
export const STAKEHOLDER_TABS = {
  stakeholder_validation: 1,
  stakeholders: 2,
  beneficiary_groups: 3,
  institutional_sponsors: 4,
} as const;

export const STAKEHOLDER_ANALYSIS_SECTION = 'stakeholder_analysis_consultation';
export const STAKEHOLDER_VALIDATION_SECTION = 'sac_stakeholder_validation';
export const BENEFICIARY_GROUPS_SECTION = 'sac_beneficiary_groups';

// The "Communities / citizens" option; its free_text holds the community engagement method.
export const COMMUNITY_STAKEHOLDER_KEY = 'stakeholder_validation_1';

// A tab of a step that saves and validates one tab at a time.
export interface StepTab {
  tabId: number;
  title: string;
  // The form fields the tab owns; edits to them count as unsaved changes.
  fields: readonly string[];
  // The project-fields sub-section the step's Save sends the fields to. A tab without one
  // saves nothing from that button: its rows save from their own popups.
  section?: string;
}

// Tab-wise steps (other than Project Overview), keyed by step, with every tab in display order.
export const STEP_TABS: Partial<Record<string, ReadonlyArray<StepTab>>> = {
  problem_tree_analysis: [
    { tabId: PROBLEM_TREE_TABS.focal_problem, title: 'Focal Problem', section: 'pta_focal_problem', fields: ['focal_problem'] },
    { tabId: PROBLEM_TREE_TABS.cause_tree, title: 'Cause Tree', fields: [] },
    { tabId: PROBLEM_TREE_TABS.effect_tree, title: 'Effect Tree', section: 'pta_effect_tree', fields: ['consequences_if_problem_remains_unaddressed'] },
    { tabId: PROBLEM_TREE_TABS.summary, title: 'Summary', section: 'pta_summary', fields: ['comprehensive_problem_tree_narrative'] },
    { tabId: PROBLEM_TREE_TABS.solution_direction, title: 'Solution Direction', section: 'pta_solution_direction', fields: ['solution_directions_high_level', 'solution_direction_narrative'] },
  ],
  // Sponsors save from their popup; the field is listed so the tab still checks there is one.
  stakeholder_analysis_and_consultation: [
    { tabId: STAKEHOLDER_TABS.stakeholder_validation, title: 'Stakeholder Validation', section: STAKEHOLDER_VALIDATION_SECTION, fields: ['stakeholder_validation', 'stakeholder_validation_remarks'] },
    { tabId: STAKEHOLDER_TABS.stakeholders, title: 'Stakeholders', fields: [] },
    { tabId: STAKEHOLDER_TABS.beneficiary_groups, title: 'Beneficiary Groups', section: BENEFICIARY_GROUPS_SECTION, fields: ['beneficiary_groups', 'beneficiary_groups_remarks'] },
    { tabId: STAKEHOLDER_TABS.institutional_sponsors, title: 'Institutional Sponsors', fields: ['institutional_sponsors'] },
  ],
  // Major input rows save from their popup; the Major Inputs tab saves the cost coverage beside them.
  preliminary_procurement_strategy: [
    { tabId: PROCUREMENT_TABS.major_inputs, title: 'Major Inputs', section: 'pps_major_inputs', fields: ['major_inputs_cost_coverage_percent'] },
    { tabId: PROCUREMENT_TABS.procurement_method, title: 'Procurement Method', section: 'pps_procurement_method', fields: ['proposed_procurement_methods'] },
    {
      tabId: PROCUREMENT_TABS.legislative_alignment,
      title: 'Legislative Alignment',
      section: 'pps_legislative_alignment',
      fields: ['ppa_alignment', 'pfma_mfma_scm_compliance', 'nt_scm_notes_fidpm_guidance'],
    },
    { tabId: PROCUREMENT_TABS.procurement_risks, title: 'Procurement Risks', section: 'pps_procurement_risks', fields: ['procurement_risks'] },
  ],
  // Options and their cost estimates save from their popups and are copied into the form for validation only.
  analysis_of_project_resource_requirements: [
    { tabId: RESOURCE_TABS.project_alternatives, title: 'Project Alternatives', fields: ['project_alternatives'] },
    { tabId: RESOURCE_TABS.cost_estimates, title: 'Preliminary Cost Estimates', fields: [] },
    { tabId: RESOURCE_TABS.sources_of_information, title: 'Sources of Information', section: 'prr_sources_of_information', fields: ['sources_of_information'] },
    { tabId: RESOURCE_TABS.concept_report_plan, title: 'Detailed Plan for Concept Report', section: 'prr_concept_report_plan', fields: ['concept_report_preparation_plan'] },
  ],
  // Objectives, means and ends, outputs, outcomes and SMART indicators save from their popups and
  // are copied into the form for validation only.
  formulation_of_project_objectives_and_strategic_alignment: [
    { tabId: OBJECTIVES_TABS.project_objectives, title: 'Project Objectives', fields: ['project_objectives_areas'] },
    { tabId: OBJECTIVES_TABS.means_to_end, title: 'Means-to-End Relationships', fields: ['means_to_end_means', 'means_to_end_ends'] },
    { tabId: OBJECTIVES_TABS.project_outputs, title: 'Project Outputs', fields: ['project_outputs'] },
    { tabId: OBJECTIVES_TABS.project_outcomes, title: 'Project Outcomes', fields: ['project_outcomes'] },
    {
      tabId: OBJECTIVES_TABS.employment_quantification,
      title: 'Employment Quantification',
      section: 'posa_employment_quantification',
      fields: ['construction_jobs', 'permanent_jobs', 'temporary_jobs'],
    },
    { tabId: OBJECTIVES_TABS.smart_indicators, title: 'SMART Indicators', fields: ['smart_indicators'] },
    {
      tabId: OBJECTIVES_TABS.organisational_alignment,
      title: 'Organisational Objective Alignment',
      section: 'posa_organisational_objective_alignment',
      fields: [
        'organisational_goals',
        'policy_or_benchmark_alignment',
        'implementation_link',
        'simple_contribution_statement',
        'strategic_alignment',
        'alignment_strength',
        'oa_organisation_priorities_aspirations',
        'oa_sector_development_plan',
        'oa_other_development_strategies',
      ],
    },
  ],
};

export const OBJECTIVES_SECTION = 'project_objectives_strategic_alignment';

export interface IEvidenceBase {
  approved_data_sources: string[];
  documents: IProjectDocument[];
  formAction?: string;
  iscompleted?: boolean;
}

export interface IWithProjectScenario {
  desired_state_description: string;
  desired_state_quantified: string;
  formAction?: string;
  iscompleted?: boolean;
}

export interface ICoreProblemAndGapAnalysis {
  core_problem_statement: string;
  cause_effect_logic: string;
  consequences: string;
  formAction?: string;
  iscompleted?: boolean;
}

export interface IKeyBusinessDrivers {
  kbd_drivers_of_intervention_need: string;
  formAction?: string;
  iscompleted?: boolean;
}

// A project_alternative project section item.
export interface IProjectAlternative {
  id: number;
  option_no: number;
  description: string;
  // Preliminary cost estimates (editor HTML), keyed by preliminary_cost_estimation_option data_key.
  cost_estimates: Record<string, string>;
}

// The preliminary_cost_estimation_option every project option must have an estimate for.
export const FINANCIAL_ESTIMATE_KEY = 'preliminary_cost_estimation_option_1';

export interface IMajorInput {
  major_input_id: number;
  title: string;
  // Rands with two decimals, e.g. "1500000.00".
  estimated_cost: string;
  specification: string | null;
  supply_source: string | null;
  operating_requirements: string | null;
  funding_source: string | null;
}

export interface IPreliminaryProcurementStrategy {
  pps_major_inputs: IMajorInput[];
  major_inputs_cost_coverage_percent: string;
  proposed_procurement_methods: string[];
  ppa_alignment: string;
  pfma_mfma_scm_compliance: string;
  nt_scm_notes_fidpm_guidance: string;
  procurement_risks: string;
  formAction?: string;
  iscompleted?: boolean;
}

export interface IProjectResourceRequirements {
  // Each option carries its own preliminary cost estimates.
  project_alternatives: IProjectAlternative[];
  sources_of_information: string;
  concept_report_preparation_plan: string;
  formAction?: string;
  iscompleted?: boolean;
}

export interface IStrategicNeed {
  strategic_need_narrative: string;
  public_value_proposition: string;
  citizen_impact: string;
  formAction?: string;
  iscompleted?: boolean;
}

// An institutional_sponsor project section item.
export interface IInstitutionalSponsor {
  id: number;
  name: string;
  // institutional_sponsor_type lookup data_key, saved as the item's "type" key.
  type: string;
  description: string;
}

export interface IStakeholderAnalysisAndConsultation {
  // Field names match the sac_stakeholder_validation project-fields section.
  stakeholder_validation: string[];
  // Option free_text; the community engagement method is under COMMUNITY_STAKEHOLDER_KEY.
  stakeholder_validation_remarks: Record<string, string>;
  // Field names match the sac_beneficiary_groups section; the UVP step only displays them.
  beneficiary_groups: string[];
  beneficiary_groups_remarks: Record<string, string>;
  institutional_sponsors: IInstitutionalSponsor[];
  formAction?: string;
  iscompleted?: boolean;
}

export interface IObjectiveMean {
  mean_id: number;
  title: string;
  description: string | null;
  cause_item_id: number;
  cause_title: string | null;
  cause_level: ProblemTreeItemLevel | null;
}

export interface IObjectiveEnd {
  end_id: number;
  title: string;
  description: string | null;
  effect_item_id: number;
  effect_title: string | null;
  effect_level: ProblemTreeItemLevel | null;
  // An End can be reached through several Means.
  mean_ids: number[];
}

export interface IMeansToEndResponse {
  means: IObjectiveMean[];
  ends: IObjectiveEnd[];
}

export interface IProjectOutput {
  output_id: number;
  title: string;
  description: string | null;
}

export type ProjectOutcomeType = 'project' | 'wcg';

// A Stage 0 outcome; id is the outcomes API outcome_id.
export interface IProjectOutcome {
  id: number;
  outcome_type: ProjectOutcomeType;
  // data_key from the WCG `outcomes` lookup; empty for a project outcome.
  wcg_outcome: string;
  outcome: string;
  description: string;
  // row_ids of the project objectives this outcome is assigned to.
  objective_row_ids: number[];
}

// A SMART indicator is a stage's KPI indicator with a target timeframe (and optional date), assigned to project outcomes.
// The indicator_measure "Text" option; its baseline and target are free text instead of numbers.
export const TEXT_INDICATOR_MEASURE_KEY = 'indicator_measure_4';

export interface IObjectiveSmartIndicator {
  kpis_indicator_id: number;
  kpi: string;
  measure: string;
  measure_display_name: string | null;
  baseline: string;
  target: string;
  // Whole years the target must be reached within; empty for indicators captured before timeframes existed.
  target_timeframe_years: number | null;
  // Optional date the target must be reached by.
  target_date: string | null;
  description: string | null;
  // ids of the project outcomes this indicator measures.
  outcome_ids: number[];
}

// The objectives, outcomes and SMART indicators of a stage, as held in that stage's step form
// (Stage 0 Project Objectives & Strategic Alignment, Stage 1 Objectives).
export interface IObjectivesOutcomesIndicators {
  project_objectives_areas: IObjectiveArea[];
  project_outcomes: IProjectOutcome[];
  smart_indicators: IObjectiveSmartIndicator[];
}

export interface IProjectObjectivesAndStrategicAlignment extends IOrganisationalObjectiveAlignment, IObjectivesOutcomesIndicators {
  means_to_end_means: IObjectiveMean[];
  means_to_end_ends: IObjectiveEnd[];
  project_outputs: IProjectOutput[];
  // Field names match the posa_employment_quantification section.
  construction_jobs: string;
  permanent_jobs: string;
  temporary_jobs: string;
  // Field names match the posa_organisational_objective_alignment section.
  oa_organisation_priorities_aspirations: string;
  oa_sector_development_plan: string;
  oa_other_development_strategies: string;
}

export interface IIndicatorTarget {
  kpis_indicator_id: number;
  kpi: string;
  measure: string;
  measure_display_name: string;
  baseline: string;
  target: string;
  description: string;
}

export interface IStakeholderTable {
  stakeholder_id?: number | null;
  stakeholder_datakey: string;
  stakeholder_name: string;
  type_datakey: string;
  type_name: string;
  roles_and_responsibilities: string;
  justification?: string;
}

export interface ISGOActivitiesAndQuests {
  key_activities_and_quests: number[];
  formAction?: string;
  iscompleted?: boolean;
}

// The UVP step's own fields. Its beneficiary groups and stakeholders are display only.
export interface IUniqueValuePropositionCompetitiveAdvantageInvestmentOpportunity {
  customer_segment: string;
  customer_need: string;
  unique_value_proposition: string;
  evidence_of_value: string;
  strategic_positioning: string;
  competitive_advantage: string;
  formAction?: string;
  iscompleted?: boolean;
}

export interface IStrategicSignificanceAndImpactFrameworkStatistics {
  // Statistics fields
  strategic_significance_score: number;
  strategic_impact_score: number;
  overall_strategic_score: number;
  strategic_fit_status: "weak" | "moderate" | "strong";
  strategic_fit_message: string;
  strategic_progress_bar_color: string;

  // Boolean requirement fields
  strategic_alignment_met: boolean;
  documented_evidence_met: boolean;
  legal_mandate_met: boolean;
  urgent_challenge_met: boolean;
  strategic_significance_all_met: boolean;

  service_improvements_met: boolean;
  institutional_changes_met: boolean;
  economic_sectors_met: boolean;
  evidence_sources_met: boolean;
  strategic_impact_all_met: boolean;

  needs_review: boolean;
}

export interface IProcurementofPlanningResources {
  request_for_additional_funding: string;
  formAction?: string;
  iscompleted?: boolean;
}

export interface IConstraintsAndRisksResponse {
  constraints_and_risks: string[];
  constraints_and_risks_remarks: Record<string, string>;
}

export interface IConstraintsAndRisks extends IConstraintsAndRisksResponse {
  formAction?: string;
  iscompleted?: boolean;
}

export type IProjectInitiationStepTypes =
  | IProjectOverviewAction
  | IExecutiveSummary
  | IFiveWOneH
  | IProblemIdentification
  | IProblemTreeAnalysis
  | IEvidenceBase
  | IWithProjectScenario
  | ICoreProblemAndGapAnalysis
  | IKeyBusinessDrivers
  | IStrategicNeed
  | IProjectResourceRequirements
  | IPreliminaryProcurementStrategy
  | IStakeholderAnalysisAndConsultation
  | IProjectObjectivesAndStrategicAlignment
  | IDocumentData
  | ISGOActivitiesAndQuests
  | IUniqueValuePropositionCompetitiveAdvantageInvestmentOpportunity
  | IProcurementofPlanningResources
  | IConstraintsAndRisks;

export interface Activity {
  id: number;
  title: string;
  description: string;
  xp: number;
  type: "required" | "recommended" | "bonus";
  is_selected: boolean;
}

export interface IKeyActivitiesAndQuestsResponse {
  required: Array<{
    key_activities_and_quests_id: number;
    title: string;
    description: string;
    points: number;
    is_selected: boolean;
  }>;
  recommended: Array<{
    key_activities_and_quests_id: number;
    title: string;
    description: string;
    points: number;
    is_selected: boolean;
  }>;
  bonus: Array<{
    key_activities_and_quests_id: number;
    title: string;
    description: string;
    points: number;
    is_selected: boolean;
  }>;
  summary: {
    selected_count: number;
    selected_points: number;
  };
}
